import React, { useMemo, useState } from 'react';
import { RotateCcwIcon, SearchIcon, Trash2Icon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { daysUntilPurge, emptyTrash, purgeLead, restoreLead } from '../../services/leadService';
import { restoreForm } from '../../services/formService';
import { restoreLanding } from '../../services/landingService';
import { formatDate, fullName } from '../../utils/format';
import { Lead } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { EmptyState } from '../../components/crm/EmptyState';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';

/**
 * Papelera de la organización. Los elementos usan borrado lógico y se
 * conservan 30 días antes de eliminarse definitivamente.
 */
export function Trash() {
  const {
    user,
    hasPermission
  } = useAuth();
  const {
    organizationId,
    trashedLeads,
    trashedForms,
    trashedLandings
  } = useWorkspace();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [confirmPurge, setConfirmPurge] = useState<Lead | 'seleccion' | 'todo' | null>(null);
  const [working, setWorking] = useState(false);
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const canDelete = hasPermission('leads.delete');
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return trashedLeads;
    return trashedLeads.filter(lead => [lead.firstName, lead.lastName, lead.company, lead.email].join(' ').toLowerCase().includes(term));
  }, [trashedLeads, search]);
  async function confirmAction() {
    setWorking(true);
    if (confirmPurge === 'todo') await emptyTrash(organizationId);else if (confirmPurge === 'seleccion') {
      for (const id of selected) await purgeLead(id);
      setSelected([]);
    } else if (confirmPurge) await purgeLead(confirmPurge.id);
    setWorking(false);
    setConfirmPurge(null);
  }
  const empty = trashedLeads.length === 0 && trashedForms.length === 0 && trashedLandings.length === 0;
  return <>
      <PageHeader title="Papelera" description="Los elementos eliminados se conservan 30 días antes de borrarse definitivamente." actions={canDelete && trashedLeads.length > 0 ? <Button variant="destructive" onClick={() => setConfirmPurge('todo')} data-id="element-1348" data-mp="1mhqb8huamos8">
              Vaciar papelera
            </Button> : undefined} data-id="element-1347" data-mp="20b7v63qw63is" />

      {empty ? <EmptyState icon={<Trash2Icon className="h-5 w-5" data-id="element-1350" data-mp="1ck2fppov5d90" />} title="La papelera está vacía" description="Aquí aparecerán los leads, formularios y landings que elimines, por si necesitas recuperarlos." data-id="element-1349" data-mp="1cjj5yw9d9v62" /> : <Tabs defaultValue="leads" data-id="element-1351" data-mp="3k4izwhq5foun">
          <TabsList data-id="element-1352" data-mp="3k7n9jumdvcsp">
            <TabsTrigger value="leads" data-id="element-1353" data-mp="3o6mdpd0aj5j3">Leads ({trashedLeads.length})</TabsTrigger>
            <TabsTrigger value="formularios" data-id="element-1354" data-mp="3t5zaoqkvlsze">
              Formularios ({trashedForms.length})
            </TabsTrigger>
            <TabsTrigger value="landings" data-id="element-1355" data-mp="207n9yyhguga">
              Landings ({trashedLandings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads" data-id="element-1356" data-mp="30iwggan8g4ey">
            <div className="relative mb-4 max-w-md" data-id="element-1357" data-mp="35gefn9bg759r">
              <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" data-id="element-1358" data-mp="35h684b9r0mvt" />
              <input type="search" aria-label="Buscar en la papelera" placeholder="Buscar por nombre, empresa o email" value={search} onChange={e => setSearch(e.target.value)} className="h-11 w-full rounded-xl border border-border bg-background pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" data-id="element-1359" data-mp="3f6zixa22gxz0" />
            </div>

            {selected.length > 0 && canDelete && <div className="mb-4 flex flex-col gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1360" data-mp="x8ja0nulbzu1">
                <p className="text-sm font-medium text-foreground" data-id="element-1361" data-mp="1bzigntbv8eaz">
                  {selected.length} seleccionados
                </p>
                <div className="flex flex-col gap-2 sm:flex-row" data-id="element-1362" data-mp="1kym04yey2vq0">
                  <Button variant="secondary" onClick={async () => {
              for (const id of selected) await restoreLead(id, authorName);
              setSelected([]);
            }} data-id="element-1363" data-mp="1fz9f8j7mvyq2">
                    Restaurar
                  </Button>
                  <Button variant="destructive" onClick={() => setConfirmPurge('seleccion')} data-id="element-1364" data-mp="1x8bnccv6oi1y">
                    Eliminar definitivamente
                  </Button>
                </div>
              </div>}

            <ul className="grid gap-3" data-id="element-1365" data-mp="1shmxf1z5jt4x">
              {filtered.map(lead => <li key={lead.id} className="flex flex-col gap-3 rounded-2xl border border-border bg-background p-4 shadow-sm sm:flex-row sm:items-center" data-id="element-1366" data-mp="1sfikxvn6tlt1">
                  {canDelete && <Checkbox id={`trash-${lead.id}`} label="" aria-label={`Seleccionar ${lead.firstName}`} checked={selected.includes(lead.id)} onChange={e => setSelected(prev => e.target.checked ? [...prev, lead.id] : prev.filter(id => id !== lead.id))} data-id="element-1367" data-mp="15igzkkmz50fe" />}
                  <div className="min-w-0 flex-1" data-id="element-1368" data-mp="7nmppv7cnaed">
                    <p className="truncate font-medium text-foreground" data-id="element-1369" data-mp="7ofyf1pvc9z5">
                      {fullName(lead.firstName, lead.lastName)}
                    </p>
                    <p className="truncate text-sm text-muted-foreground" data-id="element-1370" data-mp="zflriy7697ex">
                      {lead.company} · {lead.email}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground" data-id="element-1371" data-mp="vea8jbiszkfp">
                      Eliminado por {lead.deletedBy ?? 'Sistema'} el{' '}
                      {formatDate(lead.deletedAt ?? '')} · Se eliminará
                      definitivamente en {daysUntilPurge(lead.deletedAt)} días
                    </p>
                  </div>
                  <div className="flex flex-none gap-2" data-id="element-1372" data-mp="1ebtc3zmhsauq">
                    <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<RotateCcwIcon className="h-4 w-4" data-id="element-1374" data-mp="1qnncynqfmak5" />} onClick={() => restoreLead(lead.id, authorName)} data-id="element-1373" data-mp="1ea9uo5p3ntrd">
                      Restaurar
                    </Button>
                    {canDelete && <Button variant="destructive" size="sm" className="min-h-[44px]" onClick={() => setConfirmPurge(lead)} data-id="element-1375" data-mp="1zkxp0bb2q3q4">
                        Eliminar
                      </Button>}
                  </div>
                </li>)}
              {filtered.length === 0 && <li className="rounded-2xl border border-dashed border-border py-12 text-center text-sm text-muted-foreground" data-id="element-1376" data-mp="2ndd1db87xw4y">
                  No hay leads en la papelera.
                </li>}
            </ul>
          </TabsContent>

          <TabsContent value="formularios" data-id="element-1377" data-mp="3dozd5yml5dhj">
            <ul className="grid gap-3" data-id="element-1378" data-mp="38obcel8k1ia4">
              {trashedForms.map(form => <li key={form.id} className="flex items-center gap-3 rounded-2xl border border-border bg-background p-4 shadow-sm" data-id="element-1379" data-mp="3nkskn3lg6gsg">
                  <div className="min-w-0 flex-1" data-id="element-1380" data-mp="2uwemmnuczkgw">
                    <p className="truncate font-medium text-foreground" data-id="element-1381" data-mp="2ux6lc7j95ybe">{form.name}</p>
                    <p className="text-xs text-muted-foreground" data-id="element-1382" data-mp="2pyd5i8ghtv82">
                      Se eliminará definitivamente en {daysUntilPurge(form.deletedAt)}{' '}
                      días
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<RotateCcwIcon className="h-4 w-4" data-id="element-1384" data-mp="277c4zfzq5rme" />} onClick={() => restoreForm(form.id)} data-id="element-1383" data-mp="2zu6reig3r12i">
                    Restaurar
                  </Button>
                </li>)}
              {trashedForms.length === 0 && <li className="rounded-2xl border border-dashed border-border py-12 text-center text-sm text-muted-foreground" data-id="element-1385" data-mp="cqlyzwm8jeh0">
                  No hay formularios eliminados.
                </li>}
            </ul>
          </TabsContent>

          <TabsContent value="landings" data-id="element-1386" data-mp="15gkoft4cddm7">
            <ul className="grid gap-3" data-id="element-1387" data-mp="1aechwrkmx7si">
              {trashedLandings.map(landing => <li key={landing.id} className="flex items-center gap-3 rounded-2xl border border-border bg-background p-4 shadow-sm" data-id="element-1388" data-mp="1ac01wjyft7i3">
                  <div className="min-w-0 flex-1" data-id="element-1389" data-mp="10i9frlps995k">
                    <p className="truncate font-medium text-foreground" data-id="element-1390" data-mp="3h4kbgz793ygl">
                      {landing.name}
                    </p>
                    <p className="text-xs text-muted-foreground" data-id="element-1391" data-mp="3l7qhc5qgbnmj">
                      Se eliminará definitivamente en{' '}
                      {daysUntilPurge(landing.deletedAt)} días
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<RotateCcwIcon className="h-4 w-4" data-id="element-1393" data-mp="3sy452kpzc6u3" />} onClick={() => restoreLanding(landing.id)} data-id="element-1392" data-mp="3oy25qi7pvphz">
                    Restaurar
                  </Button>
                </li>)}
              {trashedLandings.length === 0 && <li className="rounded-2xl border border-dashed border-border py-12 text-center text-sm text-muted-foreground" data-id="element-1394" data-mp="3609am23c4jv5">
                  No hay landings eliminadas.
                </li>}
            </ul>
          </TabsContent>
        </Tabs>}

      <ConfirmDialog open={Boolean(confirmPurge)} title="Eliminar definitivamente" description="Esta acción eliminará permanentemente el lead y toda su información. No se podrá recuperar." confirmLabel="Eliminar definitivamente" loading={working} onCancel={() => setConfirmPurge(null)} onConfirm={confirmAction} data-id="element-1395" data-mp="1896fz1w6zojr" />
    </>;
}